import React, { useState, useEffect, useCallback } from 'react';
import { getTeamEvents, getDevOpsMembers, getClientHours, getWeeklyReports, getMissingImputaciones, getProactivity, getMTTR, getAIAutomation } from './api';
import './Dashboard.css';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAYS_SHORT = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamEvents, setTeamEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [clientHours, setClientHours] = useState([]);
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [imputaciones, setImputaciones] = useState([]);
  const [imputacionesWorkdays, setImputacionesWorkdays] = useState([]);
  const [proactivity, setProactivity] = useState(null);
  const [mttr, setMttr] = useState(null);
  const [aiAuto, setAiAuto] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const rangeStart = new Date(now);
      rangeStart.setDate(now.getDate() - 30);
      const rangeEnd = new Date(now);
      rangeEnd.setDate(now.getDate() + 14);

      const [eventsRes, membersRes, clientsRes, reportsRes, impRes, proactRes, mttrRes, aiAutoRes] = await Promise.all([
        getTeamEvents(rangeStart.toISOString().split('T')[0], rangeEnd.toISOString().split('T')[0]),
        getDevOpsMembers(),
        getClientHours(),
        getWeeklyReports(3),
        getMissingImputaciones(),
        getProactivity({ scope: 'ytd' }).catch(() => ({ data: null })),
        getMTTR({ scope: 'ytd' }).catch(() => ({ data: null })),
        getAIAutomation({ scope: 'ytd' }).catch(() => ({ data: null })),
      ]);

      setTeamEvents(eventsRes.data || []);
      setMembers(membersRes.data || []);
      setClientHours(clientsRes.data?.data || []);
      setWeeklyReports(reportsRes.data?.data || []);
      setImputaciones(impRes.data?.data || []);
      setImputacionesWorkdays(impRes.data?.workdays || []);
      setProactivity(proactRes.data || null);
      setMttr(mttrRes.data || null);
      setAiAuto(aiAutoRes.data || null);
      setLastRefresh(new Date());
    } catch (err) {
      setError('Error al cargar el dashboard. Verifica tu conexión.');
      console.error('[Dashboard]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (loading) return <div className="loading">Cargando dashboard...</div>;
  if (error) return (
    <div className="dash-error">
      {error}
      <button onClick={loadDashboard}>Reintentar</button>
    </div>
  );

  // ── helpers ──────────────────────────────────────────────────────────────
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const dateStr = (d) => (d ? String(d).split('T')[0] : '');

  const isActiveToday = (ev) => {
    const s = dateStr(ev.start_date);
    const e = dateStr(ev.end_date);
    return s <= todayStr && e >= todayStr;
  };

  const getShortName = (email) => {
    const m = members.find(x => x.email === email);
    if (m) {
      const parts = m.name.split(' ');
      return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : parts[0];
    }
    return email.split('@')[0];
  };

  // ── derived data ─────────────────────────────────────────────────────────
  const n1Hoy = teamEvents.find(e => e.event_type === 'N1' && isActiveToday(e));
  const n2Hoy = teamEvents.find(e => e.event_type === 'N2' && isActiveToday(e));

  const ausenciasHoy = teamEvents.filter(e => {
    const t = e.event_type.toLowerCase();
    return (t.includes('vacacion') || t === 'baja') && isActiveToday(e);
  });

  const onlineCount = members.filter(m => m.is_online).length;

  const clientsAlerta = [...clientHours]
    .filter(c => c.percentage >= 80)
    .sort((a, b) => b.percentage - a.percentage);

  // Semana actual (lun → vie)
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  const weekDays = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const getGuardiaForDay = (day, tipo) => {
    const ds = day.toISOString().split('T')[0];
    const ev = teamEvents.find(e => {
      const s = dateStr(e.start_date);
      const en = dateStr(e.end_date);
      return e.event_type === tipo && s <= ds && en >= ds;
    });
    return ev ? getShortName(ev.member_email) : null;
  };

  const getMemberBadge = (email) => {
    if (ausenciasHoy.some(e => e.member_email === email)) return { label: 'Vacaciones', cls: 'dash-badge-vac' };
    if (n1Hoy?.member_email === email) return { label: 'N1', cls: 'dash-badge-n1' };
    if (n2Hoy?.member_email === email) return { label: 'N2', cls: 'dash-badge-n2' };
    return null;
  };

  // Horas semanales
  const uniqueWeeks = [...new Set(weeklyReports.map(r => dateStr(r.week_start)))]
    .sort().reverse().slice(0, 2);

  const hoursByUser = {};
  weeklyReports.forEach(r => {
    const ws = dateStr(r.week_start);
    if (!uniqueWeeks.includes(ws)) return;
    if (!hoursByUser[r.user_email]) hoursByUser[r.user_email] = { name: r.user_name, weeks: {} };
    hoursByUser[r.user_email].weeks[ws] = parseFloat(r.hours) || 0;
  });

  const formatWeekLabel = (ws) => {
    if (!ws) return '';
    const d = new Date(ws + 'T00:00:00');
    const end = new Date(d);
    end.setDate(d.getDate() + 4);
    return `${d.getDate()}/${d.getMonth() + 1} – ${end.getDate()}/${end.getMonth() + 1}`;
  };

  const clientSeverity = (pct) =>
    pct >= 100
      ? { cls: 'dc-sev-red', icon: '🔴' }
      : { cls: 'dc-sev-orange', icon: '🟠' };

  // Imputaciones: solo miembros con algún día < 7.5h
  const MIN_HOURS = 7.5;
  const impWithProblems = imputaciones.filter(m =>
    m.days.some(d => d.hours !== null && d.hours < MIN_HOURS)
  );

  const impDayLabel = (dateS) => {
    const d = new Date(dateS + 'T00:00:00');
    return `${DAYS_SHORT[d.getDay()]} ${d.getDate()}`;
  };

  const impCellClass = (hours) => {
    if (hours === null) return 'imp-cell-na';
    if (hours >= MIN_HOURS) return 'imp-cell-ok';
    if (hours > 0) return 'imp-cell-warn';
    return 'imp-cell-miss';
  };

  const impCellText = (hours) => {
    if (hours === null) return '—';
    if (hours >= MIN_HOURS) return `${hours.toFixed(1)}h ✓`;
    if (hours > 0) return `${hours.toFixed(1)}h`;
    return '0h ✗';
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard">

      {/* Header */}
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Dashboard</h2>
          <span className="dash-subtitle">
            {DAYS_SHORT[now.getDay()]}, {now.getDate()} de {MONTHS[now.getMonth()]} de {now.getFullYear()}
          </span>
        </div>
        <button className="dash-refresh" onClick={loadDashboard}>
          ↻ Actualizar
          {lastRefresh && (
            <span className="dash-refresh-time">
              {lastRefresh.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="dash-kpi-row">

        <div className="kpi-card kpi-guardia">
          <div className="kpi-label">Guardia hoy</div>
          <div className="kpi-body">
            {n1Hoy ? (
              <>
                <div className="kpi-guard-line">
                  <span className="gtag gtag-n1">N1</span>
                  <span className="kpi-guard-name">{getShortName(n1Hoy.member_email)}</span>
                </div>
                {n2Hoy && (
                  <div className="kpi-guard-line">
                    <span className="gtag gtag-n2">N2</span>
                    <span className="kpi-guard-name">{getShortName(n2Hoy.member_email)}</span>
                  </div>
                )}
              </>
            ) : (
              <span className="kpi-empty">Sin asignar</span>
            )}
          </div>
        </div>

        <div className="kpi-card kpi-ausencias">
          <div className="kpi-label">Ausencias hoy</div>
          <div className="kpi-body">
            <div className={`kpi-big ${ausenciasHoy.length === 0 ? 'kpi-ok' : ''}`}>
              {ausenciasHoy.length}
            </div>
            <div className="kpi-sub">
              {ausenciasHoy.length === 0
                ? 'Todo el equipo disponible'
                : ausenciasHoy.slice(0, 2).map(e => getShortName(e.member_email)).join(', ') +
                  (ausenciasHoy.length > 2 ? '…' : '')}
            </div>
          </div>
        </div>

        <div className="kpi-card kpi-alertas">
          <div className="kpi-label">Alertas de clientes</div>
          <div className="kpi-body">
            {clientsAlerta.length > 0 ? (
              <div className="kpi-alert-pills">
                {clientsAlerta.filter(c => c.percentage >= 100).length > 0 && (
                  <span className="dash-pill dash-pill-red">
                    🔴 {clientsAlerta.filter(c => c.percentage >= 100).length} excedidos
                  </span>
                )}
                {clientsAlerta.filter(c => c.percentage >= 80 && c.percentage < 100).length > 0 && (
                  <span className="dash-pill dash-pill-orange">
                    🟠 {clientsAlerta.filter(c => c.percentage >= 80 && c.percentage < 100).length} en aviso
                  </span>
                )}
              </div>
            ) : (
              <>
                <div className="kpi-big kpi-ok">✓</div>
                <div className="kpi-sub">Sin alertas este mes</div>
              </>
            )}
          </div>
        </div>

        <div className="kpi-card kpi-online">
          <div className="kpi-label">Equipo online</div>
          <div className="kpi-body">
            <div className="kpi-big">
              {onlineCount}<span className="kpi-total">/{members.length}</span>
            </div>
            <div className="kpi-sub">
              {onlineCount === 0 ? 'Nadie conectado' : `${onlineCount} activo${onlineCount !== 1 ? 's' : ''} ahora`}
            </div>
          </div>
        </div>

        {proactivity?.global && (
          <div className={`kpi-card kpi-proactivity ${proactivity.global.meetsTarget ? 'kpi-target-ok' : 'kpi-target-ko'}`}>
            <div className="kpi-label">Proactividad YTD</div>
            <div className="kpi-body">
              <div className="kpi-big">
                {proactivity.global.proactivePct}<span className="kpi-total">%</span>
              </div>
              <div className="kpi-sub">
                Objetivo &gt;30% · {proactivity.global.meetsTarget ? '✓ dentro' : '⚠ fuera'}
              </div>
            </div>
          </div>
        )}

        {mttr && mttr.totalIncidents > 0 && (
          <div className={`kpi-card kpi-mttr ${mttr.meetsTarget ? 'kpi-target-ok' : 'kpi-target-ko'}`}>
            <div className="kpi-label">MTTR con IA YTD</div>
            <div className="kpi-body">
              <div className="kpi-big">
                {mttr.reductionPct > 0 ? '-' : ''}{Math.abs(mttr.reductionPct)}<span className="kpi-total">%</span>
              </div>
              <div className="kpi-sub">
                Objetivo -35% · {mttr.meetsTarget ? '✓ dentro' : '⚠ fuera'}
              </div>
            </div>
          </div>
        )}

        {aiAuto && aiAuto.totalArtifacts > 0 && (
          <div className={`kpi-card kpi-aiauto ${aiAuto.meetsTarget ? 'kpi-target-ok' : 'kpi-target-ko'}`}>
            <div className="kpi-label">Runbooks con IA YTD</div>
            <div className="kpi-body">
              <div className="kpi-big">
                {aiAuto.aiPct}<span className="kpi-total">%</span>
              </div>
              <div className="kpi-sub">
                Objetivo ≥40% · {aiAuto.meetsTarget ? '✓ dentro' : '⚠ fuera'}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Estado equipo + guardias semana */}
      <div className="dash-grid-2">

        <div className="dash-panel">
          <h3 className="panel-title">Estado del equipo</h3>
          <div className="dash-team-list">
            {members.map(m => {
              const badge = getMemberBadge(m.email);
              return (
                <div key={m.email} className="dash-team-row">
                  <span className={`dash-dot ${m.is_online ? 'dash-dot-on' : 'dash-dot-off'}`} />
                  <span className="dash-member-name">{getShortName(m.email)}</span>
                  {m.role === 'admin' && <span className="dash-role-admin">Admin</span>}
                  {badge && <span className={`dash-status-badge ${badge.cls}`}>{badge.label}</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="dash-panel">
          <h3 className="panel-title">Guardias esta semana</h3>
          <div className="week-grid">
            <div className="week-row week-header">
              <span />
              {weekDays.map((d, i) => (
                <span key={i} className={`day-head ${d.toISOString().split('T')[0] === todayStr ? 'day-today' : ''}`}>
                  {DAYS_SHORT[d.getDay()]}<br /><small>{d.getDate()}</small>
                </span>
              ))}
            </div>
            {['N1', 'N2'].map(tipo => (
              <div key={tipo} className="week-row">
                <span className={`gtag ${tipo === 'N1' ? 'gtag-n1' : 'gtag-n2'}`}>{tipo}</span>
                {weekDays.map((d, i) => {
                  const name = getGuardiaForDay(d, tipo);
                  const isToday = d.toISOString().split('T')[0] === todayStr;
                  return (
                    <span key={i} className={`gcell ${isToday ? 'gcell-today' : ''}`}>
                      {name
                        ? <span className="gcell-name">{name}</span>
                        : <span className="gcell-empty">—</span>}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Imputaciones diarias */}
      {imputacionesWorkdays.length > 0 && (
        <div className="dash-panel">
          <h3 className="panel-title">Imputaciones — días laborables de esta semana</h3>
          {impWithProblems.length === 0 ? (
            <div className="imp-panel-ok">✓ Todo el equipo ha imputado correctamente</div>
          ) : (
            <table className="imp-table">
              <thead>
                <tr>
                  <th>Persona</th>
                  {imputacionesWorkdays.map(d => <th key={d}>{impDayLabel(d)}</th>)}
                </tr>
              </thead>
              <tbody>
                {imputaciones.map(member => {
                  const hasProblem = member.days.some(d => d.hours !== null && d.hours < MIN_HOURS);
                  if (!hasProblem) return null;
                  return (
                    <tr key={member.email}>
                      <td className="imp-person">{member.name.split(' ').slice(0, 2).join(' ')}</td>
                      {member.days.map(d => (
                        <td key={d.date} className={impCellClass(d.hours)}>
                          {impCellText(d.hours)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Clientes en alerta */}
      {clientsAlerta.length > 0 && (
        <div className="dash-panel">
          <h3 className="panel-title">
            Clientes en alerta — {MONTHS[now.getMonth()]} {now.getFullYear()}
          </h3>
          <div className="dc-list">
            {clientsAlerta.map(c => {
              const { cls, icon } = clientSeverity(c.percentage);
              return (
                <div key={c.id} className={`dc-row ${cls}`}>
                  <div className="dc-info">
                    <span className="dc-icon">{icon}</span>
                    <span className="dc-name">{c.name}</span>
                  </div>
                  <div className="dc-bar-wrap">
                    <div className="dc-bar">
                      <div
                        className="dc-bar-fill"
                        style={{ width: `${Math.min(c.percentage, 100)}%` }}
                      />
                    </div>
                    <span className="dc-pct">{Math.round(c.percentage)}%</span>
                  </div>
                  <div className="dc-hours">
                    {Math.round(c.consumedHours)}h / {Math.round(c.monthlyHours)}h
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Proactividad detalle */}
      {proactivity?.global && proactivity.global.totalHours > 0 && (
        <div className="dash-panel">
          <h3 className="panel-title">
            Proactividad — {proactivity.year} YTD
            <span className="panel-subtitle"> (objetivo: proactivo &gt; 30%)</span>
          </h3>

          <div className="proactivity-bar">
            <div className="proactivity-bar-track">
              <div
                className="proactivity-bar-seg proactivity-proactivo"
                style={{ width: `${proactivity.global.breakdown.proactivo.pct}%` }}
                title={`Proactivo: ${proactivity.global.breakdown.proactivo.hours}h (${proactivity.global.breakdown.proactivo.pct}%)`}
              />
              <div
                className="proactivity-bar-seg proactivity-reactivo"
                style={{ width: `${proactivity.global.breakdown.reactivo.pct}%` }}
                title={`Reactivo: ${proactivity.global.breakdown.reactivo.hours}h (${proactivity.global.breakdown.reactivo.pct}%)`}
              />
            </div>
            <div className="proactivity-legend">
              <span><i className="proactivity-dot proactivity-proactivo" /> Proactivo {proactivity.global.breakdown.proactivo.pct}% ({proactivity.global.breakdown.proactivo.hours}h)</span>
              <span><i className="proactivity-dot proactivity-reactivo" /> Reactivo {proactivity.global.breakdown.reactivo.pct}% ({proactivity.global.breakdown.reactivo.hours}h)</span>
            </div>
          </div>

          {proactivity.aggregateView && proactivity.byUser && proactivity.byUser.length > 0 && (
            <table className="dash-hours-table proactivity-table">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th className="text-right">Horas</th>
                  <th className="text-right">Proactivo</th>
                  <th className="text-right">Reactivo</th>
                  <th className="text-right">Ratio</th>
                </tr>
              </thead>
              <tbody>
                {proactivity.byUser.map(u => (
                  <tr key={u.redmineUserId}>
                    <td className="dash-hours-person">{u.name}</td>
                    <td className="text-right">{u.totalHours.toFixed(1)}h</td>
                    <td className="text-right">{u.breakdown.proactivo.hours.toFixed(1)}h</td>
                    <td className="text-right">{u.breakdown.reactivo.hours.toFixed(1)}h</td>
                    <td className="text-right">
                      <span className={`proactivity-badge ${u.meetsTarget ? 'ok' : 'ko'}`}>
                        {u.proactivePct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="proactivity-note">
            Cuenta como proactivo cualquier imputación cuyo <strong>título del issue</strong> o <strong>comentario</strong> contenga la palabra "proactivo".
            Datos basados en {proactivity.global.totalHours}h imputadas.
          </div>
        </div>
      )}

      {/* MTTR - Incidencias asistidas por IA */}
      {mttr && mttr.totalIncidents > 0 && (
        <div className="dash-panel">
          <h3 className="panel-title">
            Incidencias — {mttr.year} YTD
            <span className="panel-subtitle"> (objetivo: -35% horas/incidencia con IA)</span>
          </h3>

          <div className="mttr-grid">
            <div className="mttr-block mttr-block-ai">
              <div className="mttr-block-label">Con IA</div>
              <div className="mttr-block-big">{mttr.withAI.avgHours}h</div>
              <div className="mttr-block-sub">media/incidencia</div>
              <div className="mttr-block-count">{mttr.withAI.count} incidencias · {mttr.withAI.totalHours}h totales</div>
            </div>

            <div className="mttr-block mttr-block-noai">
              <div className="mttr-block-label">Sin IA</div>
              <div className="mttr-block-big">{mttr.withoutAI.avgHours}h</div>
              <div className="mttr-block-sub">media/incidencia</div>
              <div className="mttr-block-count">{mttr.withoutAI.count} incidencias · {mttr.withoutAI.totalHours}h totales</div>
            </div>

            <div className={`mttr-block mttr-block-delta ${mttr.meetsTarget ? 'ok' : 'ko'}`}>
              <div className="mttr-block-label">Reducción</div>
              <div className="mttr-block-big">
                {mttr.reductionPct > 0 ? '-' : ''}{Math.abs(mttr.reductionPct)}%
              </div>
              <div className="mttr-block-sub">vs sin IA</div>
              <div className="mttr-block-count">
                {mttr.meetsTarget ? '✓ Cumple objetivo -35%' : '⚠ Fuera de objetivo -35%'}
              </div>
            </div>
          </div>

          {mttr.topIncidents.length > 0 && (
            <details className="mttr-details">
              <summary>Top {mttr.topIncidents.length} incidencias por horas</summary>
              <table className="dash-hours-table mttr-table">
                <thead>
                  <tr>
                    <th>Issue</th>
                    <th>Asunto</th>
                    <th className="text-right">Horas</th>
                    <th className="text-right">IA</th>
                  </tr>
                </thead>
                <tbody>
                  {mttr.topIncidents.map(i => (
                    <tr key={i.issueId || i.subject}>
                      <td>#{i.issueId || '-'}</td>
                      <td>{i.subject}</td>
                      <td className="text-right">{i.totalHours}h</td>
                      <td className="text-right">{i.aiAssisted ? '🤖' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          <div className="proactivity-note">
            Cuenta como incidencia cualquier imputación cuyo <strong>título del issue</strong> o <strong>comentario</strong> contenga "incidencia".
            Marca como asistida por IA si contiene <code>ia</code>, <code>ai</code>, <code>[ia]</code>, <code>con-ia</code> o <code>asistido-ia</code>.
          </div>
        </div>
      )}

      {/* AI Automation - Runbooks/Deploys */}
      {aiAuto && aiAuto.totalArtifacts > 0 && (
        <div className="dash-panel">
          <h3 className="panel-title">
            Runbooks y despliegues — {aiAuto.year} YTD
            <span className="panel-subtitle"> (objetivo: ≥40% generados o validados por IA)</span>
          </h3>

          <div className="mttr-grid">
            <div className="mttr-block mttr-block-ai">
              <div className="mttr-block-label">Con IA</div>
              <div className="mttr-block-big">{aiAuto.aiArtifacts}</div>
              <div className="mttr-block-sub">artefactos</div>
              <div className="mttr-block-count">{aiAuto.aiPct}% del total</div>
            </div>

            <div className="mttr-block mttr-block-noai">
              <div className="mttr-block-label">Manual</div>
              <div className="mttr-block-big">{aiAuto.manualArtifacts}</div>
              <div className="mttr-block-sub">artefactos</div>
              <div className="mttr-block-count">{(100 - aiAuto.aiPct).toFixed(1)}% del total</div>
            </div>

            <div className={`mttr-block mttr-block-delta ${aiAuto.meetsTarget ? 'ok' : 'ko'}`}>
              <div className="mttr-block-label">Ratio IA</div>
              <div className="mttr-block-big">{aiAuto.aiPct}%</div>
              <div className="mttr-block-sub">objetivo ≥40%</div>
              <div className="mttr-block-count">
                {aiAuto.meetsTarget ? '✓ Cumple objetivo' : '⚠ Fuera de objetivo'}
              </div>
            </div>
          </div>

          {aiAuto.topArtifacts.length > 0 && (
            <details className="mttr-details">
              <summary>Top {aiAuto.topArtifacts.length} artefactos por horas</summary>
              <table className="dash-hours-table mttr-table">
                <thead>
                  <tr>
                    <th>Issue</th>
                    <th>Asunto</th>
                    <th className="text-right">Horas</th>
                    <th className="text-right">IA</th>
                  </tr>
                </thead>
                <tbody>
                  {aiAuto.topArtifacts.map(a => (
                    <tr key={a.issueId || a.subject}>
                      <td>#{a.issueId || '-'}</td>
                      <td>{a.subject}</td>
                      <td className="text-right">{a.totalHours}h</td>
                      <td className="text-right">{a.aiAssisted ? '🤖' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          <div className="proactivity-note">
            Cuenta como artefacto cualquier issue cuyo <strong>título</strong> o <strong>comentario</strong> contenga <code>runbook</code>, <code>despliegue</code> o <code>deploy</code>.
            Se marca como asistido/generado por IA si además contiene <code>ia</code>, <code>[ia]</code>, <code>con-ia</code> o <code>asistido-ia</code>.
          </div>
        </div>
      )}

      {/* Horas semanales */}
      {uniqueWeeks.length > 0 && Object.keys(hoursByUser).length > 0 && (
        <div className="dash-panel">
          <h3 className="panel-title">Horas imputadas — últimas semanas</h3>
          <table className="dash-hours-table">
            <thead>
              <tr>
                <th>Persona</th>
                {uniqueWeeks.map(w => <th key={w}>{formatWeekLabel(w)}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.entries(hoursByUser).map(([email, data]) => (
                <tr key={email}>
                  <td className="dash-hours-person">{data.name.split(' ').slice(0, 2).join(' ')}</td>
                  {uniqueWeeks.map(w => {
                    const h = data.weeks[w] || 0;
                    const cls = h > 0 && h < 30 ? 'h-low' : h > 45 ? 'h-high' : '';
                    return (
                      <td key={w} className={`dash-hours-cell ${cls}`}>
                        {h > 0 ? `${h.toFixed(1)}h` : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

export default Dashboard;
